import { PurchaseOrderStatusLabels } from '@supply-mover-2-0/shared';
import type { ListBuilderColumn, ListBuilderConfiguration } from '../types';

const PURCHASE_ORDER_COLUMNS: Array<ListBuilderColumn> = [
	{
		id: 'po-number',
		fields: [
			{
				id: 'po-number-field',
				link: {
					type: 'object',
					enabled: true,
					objectTable: 'purchase_order',
					idField: '_id',
				},
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'externalId',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'theme',
						themeColor: 'primary',
					},
				},
				displayStyle: 'text',
			},
			{
				id: 'po-id-field',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: '_id',
					},
				},
				styleConfig: {
					size: 'xs',
					colorRule: {
						type: 'theme',
						themeColor: 'textSecondary',
					},
				},
				displayStyle: 'text',
			},
		],
		headers: [
			{ id: 'h1', text: 'PO Number' },
			{ id: 'h1-sub', text: 'ID' },
		],
		minWidth: 120,
		sortable: true,
		alignment: 'left',
		sortFields: [
			{
				direction: 'ASC',
				fieldName: 'externalId',
			},
		],
		widthConfig: {
			mode: 'flexible',
			preset: 'medium',
		},
		verticalAlignment: 'middle',
	},
	{
		id: 'po-vendor',
		fields: [
			{
				id: 'po-vendor-field',
				// PD-14564: link to the vendor detail. The payload exposes a flat
				// `vendorId` (PD-14568), so the object link resolves like
				// call_history's vendor link.
				link: {
					type: 'object',
					enabled: true,
					idField: 'vendorId',
					objectTable: 'vendor',
				},
				dataConfig: {
					type: 'field',
					// PD-14568 attaches a nested `vendor` object to the payload;
					// the renderer reads dotted paths (getNestedFieldValue), so
					// `vendor.name` resolves record.vendor.name.
					field: {
						fieldName: 'vendor.name',
					},
					defaultValue: '-',
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'theme',
						themeColor: 'primary',
					},
				},
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h-vendor', text: 'Vendor' }],
		minWidth: 140,
		sortable: false,
		alignment: 'left',
		widthConfig: {
			mode: 'flexible',
			preset: 'medium',
		},
		verticalAlignment: 'middle',
	},
	{
		id: 'status',
		fields: [
			{
				id: 'status-field',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'statusLabel',
					},
					defaultValue: 'Unknown',
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'conditional',
						rules: [
							{
								field: { fieldName: 'status' },
								operator: 'equals',
								value: '0',
								color: { type: 'neutral' },
							},
							{
								field: { fieldName: 'status' },
								operator: 'equals',
								value: '1',
								color: { type: 'info' },
							},
							{
								field: { fieldName: 'status' },
								operator: 'equals',
								value: '2',
								color: { type: 'success' },
							},
							{
								field: { fieldName: 'status' },
								operator: 'equals',
								value: '3',
								color: { type: 'info' },
							},
							{
								field: { fieldName: 'status' },
								operator: 'equals',
								value: '4',
								color: { type: 'warning' },
							},
							{
								field: { fieldName: 'status' },
								operator: 'equals',
								value: '5',
								color: { type: 'success' },
							},
							{
								field: { fieldName: 'status' },
								operator: 'equals',
								value: '6',
								color: { type: 'warning' },
							},
							{
								field: { fieldName: 'status' },
								operator: 'equals',
								value: '7',
								color: { type: 'neutral' },
							},
							{
								field: { fieldName: 'status' },
								operator: 'equals',
								value: '8',
								color: { type: 'warning' },
							},
							{
								field: { fieldName: 'status' },
								operator: 'equals',
								value: '9',
								color: { type: 'danger' },
							},
							{
								field: { fieldName: 'status' },
								operator: 'equals',
								value: '10',
								color: { type: 'info' },
							},
						],
						defaultColor: { type: 'neutral' },
					},
				},
				displayStyle: 'badge',
			},
		],
		headers: [{ id: 'h2', text: 'Status' }],
		minWidth: 140,
		sortable: true,
		alignment: 'left',
		sortFields: [
			{
				direction: 'ASC',
				fieldName: 'status',
			},
		],
		widthConfig: {
			mode: 'flexible',
			preset: 'medium',
		},
		verticalAlignment: 'middle',
	},
	{
		id: 'requested-date',
		fields: [
			{
				id: 'requested-date-field',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'requestedDate',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'default',
					},
				},
				displayStyle: 'date',
			},
		],
		headers: [{ id: 'h3', text: 'Requested Date' }],
		minWidth: 120,
		sortable: true,
		alignment: 'center',
		sortFields: [
			{
				direction: 'DESC',
				fieldName: 'requestedDate',
			},
		],
		widthConfig: {
			mode: 'flexible',
			preset: 'medium',
		},
		verticalAlignment: 'middle',
	},
	{
		id: 'order-date',
		fields: [
			{
				id: 'order-date-field',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'date',
						objectTable: 'purchase_order',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'default',
					},
				},
				displayStyle: 'date',
			},
		],
		headers: [{ id: 'h4', text: 'Order Date' }],
		minWidth: 100,
		sortable: true,
		alignment: 'center',
		sortFields: [
			{
				direction: 'DESC',
				fieldName: 'date',
				objectTable: 'purchase_order',
			},
		],
		widthConfig: {
			mode: 'flexible',
			preset: 'small',
		},
		verticalAlignment: 'middle',
	},
	{
		// Follow-up date — when the buyer planned to chase the vendor
		// for a quote response. Renders blank for POs that don't have
		// one set (most non-Quote POs). Used by the Quotes — Follow-Up
		// list as the default sort so the longest-pending follow-ups
		// surface at the top.
		id: 'follow-up-date',
		fields: [
			{
				id: 'follow-up-date-field',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'followUpDate',
						objectTable: 'purchase_order',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'default',
					},
				},
				displayStyle: 'date',
			},
		],
		headers: [{ id: 'h-follow-up', text: 'Follow-Up' }],
		minWidth: 110,
		sortable: true,
		alignment: 'center',
		sortFields: [
			{
				direction: 'ASC',
				fieldName: 'followUpDate',
				objectTable: 'purchase_order',
			},
		],
		widthConfig: {
			mode: 'flexible',
			preset: 'small',
		},
		verticalAlignment: 'middle',
	},
	{
		id: 'item-count',
		fields: [
			{
				id: 'item-count-field',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'itemCount',
					},
					defaultValue: '0',
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'default',
					},
				},
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h5', text: 'Items' }],
		minWidth: 80,
		sortable: true,
		alignment: 'center',
		sortFields: [
			{
				direction: 'DESC',
				fieldName: 'itemCount',
			},
		],
		widthConfig: {
			mode: 'flexible',
			preset: 'small',
		},
		verticalAlignment: 'middle',
	},
	{
		id: 'total',
		fields: [
			{
				id: 'total-field',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'total',
					},
					defaultValue: '0.00',
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'default',
					},
				},
				displayStyle: 'currency',
			},
		],
		headers: [{ id: 'h6', text: 'Total' }],
		minWidth: 100,
		sortable: true,
		alignment: 'right',
		sortFields: [
			{
				direction: 'DESC',
				fieldName: 'total',
			},
		],
		widthConfig: {
			mode: 'flexible',
			preset: 'small',
		},
		verticalAlignment: 'middle',
	},
	{
		id: 'actions',
		fields: [
			{
				id: 'actions-dropdown',
				dataConfig: {
					type: 'custom_text',
					customText: '',
				},
				styleConfig: {
					size: 'md',
					colorRule: {
						type: 'default',
					},
				},
				displayStyle: 'actions_dropdown',
				dropdownActions: [
					{
						id: 'edit-action',
						label: 'Edit',
						iconName: 'edit',
						callbackName: 'edit',
						variant: 'primary',
						visibility: 'visible',
					},
					{
						id: 'delete-action',
						label: 'Delete',
						iconName: 'trash',
						callbackName: 'delete',
						variant: 'danger',
						visibility: 'visible',
					},
				],
			},
		],
		headers: [{ id: 'actions-header', text: '' }],
		width: 80,
		align: 'center',
		sortable: false,
	},
];

const PURCHASE_ORDER_REQUIRED_FIELDS = [
	'purchaseOrder.id',
	'purchaseOrder.externalId',
	'purchaseOrder.status',
	'purchaseOrder.date',
	'purchaseOrder.requestedDate',
	'purchaseOrder.vendorId',
];

/**
 * Purchase Order list configuration
 * Shows all purchase orders with status badges, dates, and edit/delete actions
 * Features conditional color rules for status values (incl. '10' Quote)
 * Converted from JSON to TypeScript for type safety
 */
export const PURCHASE_ORDER_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-purchase-orders-all',
			cacheTTL: 600000,
			name: 'All Purchase Orders',
			type: 'default',
			active: true,
			columns: PURCHASE_ORDER_COLUMNS,
			description: 'List of purchase orders for vendors',
			defaultSortId: 'column-order-date-desc',
			requiredFields: PURCHASE_ORDER_REQUIRED_FIELDS,
			sortOrder: 0,
		},
		{
			// PD-14671: quote tabs yield their front-of-strip positions to
			// the pipeline stage tabs (Pending Approval / Ready to Issue /
			// Awaiting Receipt) — the stages a purchasing lead watches were
			// the ones truncated into the More overflow.
			id: 'default-purchase-orders-outstanding-quotes',
			cacheTTL: 300000,
			name: 'Outstanding Quotes',
			type: 'default',
			active: true,
			columns: PURCHASE_ORDER_COLUMNS,
			description:
				'Purchase orders currently in QUOTE status — vendor quotes / RFQs awaiting buyer review or response.',
			defaultSortId: 'column-order-date-desc',
			requiredFields: PURCHASE_ORDER_REQUIRED_FIELDS,
			sortOrder: 6,
			filterRules: {
				purchaseOrderStatus: {
					defaultValue: [{ id: '10', label: 'Quote' }],
				},
			},
		},
		{
			id: 'default-purchase-orders-quotes-follow-up',
			cacheTTL: 300000,
			name: 'Quotes — Follow-Up',
			type: 'default',
			active: true,
			columns: PURCHASE_ORDER_COLUMNS,
			description:
				'QUOTE-status POs that have a follow-up date set, sorted oldest-first so the most overdue chase is at the top.',
			defaultSortId: 'column-follow-up-date-asc',
			requiredFields: PURCHASE_ORDER_REQUIRED_FIELDS,
			sortOrder: 7,
			filterRules: {
				purchaseOrderStatus: {
					defaultValue: [{ id: '10', label: 'Quote' }],
				},
				// Effective `followUpDate IS NOT NULL` — we don't have a
				// dedicated is-not-null filter type, but a far-past
				// `startDate` matches every non-null row since followUpDate
				// is always after 1970-01-01. Locked + hidden because the
				// user's mental model for this tab is "follow-ups, period",
				// not "follow-ups in date range X".
				purchaseOrderFollowUpDateRange: {
					defaultValue: {
						startDate: '1900-01-01',
						endDate: '',
					},
					locked: true,
					hidden: true,
				},
			},
		},
		{
			// The Purchasing Manager's queue. Locked filter on
			// approvalStatus = 'submitted' so the tab IS the queue —
			// no need for the manager to set a filter every time.
			// Ordered by oldest-first so the most overdue review
			// surfaces at the top.
			id: 'default-purchase-orders-pending-approval',
			cacheTTL: 60000,
			name: 'Pending Approval',
			type: 'default',
			active: true,
			columns: PURCHASE_ORDER_COLUMNS,
			description:
				'POs awaiting Purchasing Manager review. Approve or reject from the PO detail page.',
			defaultSortId: 'column-order-date-asc',
			requiredFields: PURCHASE_ORDER_REQUIRED_FIELDS,
			sortOrder: 1,
			filterRules: {
				purchaseOrderApprovalStatus: {
					defaultValue: [{ id: 'submitted', label: 'Submitted for Approval' }],
					locked: true,
					hidden: true,
				},
			},
		},
		{
			// PD-14671: the owner's "Pending Issue" stage — POs cleared to
			// go (Approved, status '2') that nobody has issued to the vendor
			// yet. Without this tab the parking state between approval and
			// issue was invisible outside each PO's detail page.
			id: 'default-purchase-orders-ready-to-issue',
			cacheTTL: 60000,
			name: 'Ready to Issue',
			type: 'default',
			active: true,
			columns: PURCHASE_ORDER_COLUMNS,
			description:
				'Approved POs not yet issued to the vendor — cleared to go, waiting on the Issue click.',
			defaultSortId: 'column-order-date-asc',
			requiredFields: PURCHASE_ORDER_REQUIRED_FIELDS,
			sortOrder: 2,
			filterRules: {
				purchaseOrderStatus: {
					defaultValue: [{ id: '2', label: PurchaseOrderStatusLabels['2'] }],
					locked: true,
					hidden: true,
				},
			},
		},
		{
			// Phase 8.2 — RELEASED POs that haven't been fully received
			// yet. Receiving / purchasing teams use this as the
			// "what's coming in" view. Status filter is the simple
			// proxy — once a PO hits PARTIALLY_RECEIVED or RECEIVED
			// the receipt rollup has progressed and it drops off.
			// Future enhancement: add a `purchaseOrderHasOpenQty`
			// filter that joins through po_detail to count remaining
			// units, so partially-received POs with open lines stay
			// visible. v1 ships with the simpler RELEASED-only view.
			id: 'default-purchase-orders-awaiting-receipt',
			cacheTTL: 300000,
			name: 'Awaiting Receipt',
			type: 'default',
			active: true,
			columns: PURCHASE_ORDER_COLUMNS,
			description:
				'POs issued to the vendor but not yet received. The receiving team\'s "what\'s coming in" view.',
			defaultSortId: 'requested-date',
			requiredFields: PURCHASE_ORDER_REQUIRED_FIELDS,
			sortOrder: 3,
			filterRules: {
				purchaseOrderStatus: {
					// PD-14595: labels come from the shared map so the
					// default chips match the filter options verbatim.
					defaultValue: [
						{ id: '3', label: PurchaseOrderStatusLabels['3'] },
						{ id: '4', label: PurchaseOrderStatusLabels['4'] },
						{ id: '8', label: PurchaseOrderStatusLabels['8'] },
					],
				},
			},
		},
		{
			// PD-14666: the consolidated vendor-claims queue — every PO
			// carrying at least one receipt exception not yet Resolved /
			// Written Off. Reads the denormalized openExceptionCount the
			// exception recompute path maintains (D7's EXISTS filter,
			// expressed as a plain registry predicate). Locked + hidden:
			// the tab IS the filter.
			id: 'default-purchase-orders-outstanding-vendor-issues',
			cacheTTL: 60000,
			name: 'Outstanding Vendor Issues',
			type: 'default',
			active: true,
			columns: PURCHASE_ORDER_COLUMNS,
			description:
				'POs with open receiving issues (damaged / short / wrong item / refused) — unresolved vendor claims.',
			defaultSortId: 'column-order-date-asc',
			requiredFields: PURCHASE_ORDER_REQUIRED_FIELDS,
			sortOrder: 4,
			filterRules: {
				purchaseOrderHasOpenIssues: {
					defaultValue: { id: 'true', label: 'Yes' },
					locked: true,
					hidden: true,
				},
			},
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [
			'purchaseOrderKeywords',
			'purchaseOrderStatus',
			'purchaseOrderApprovalStatus',
			'purchaseOrderVendor',
			// PD-14666: has-open-receiving-issues toggle (drives the
			// Outstanding Vendor Issues tab; usable standalone too).
			'purchaseOrderHasOpenIssues',
			// PD-14690: slice the open-issues queue by what went wrong,
			// claim progress, claim owner, and how long it has been open.
			'purchaseOrderIssueType',
			'purchaseOrderIssueStatus',
			'purchaseOrderIssueAssignee',
			'purchaseOrderIssueAge',
			'purchaseOrderExpirationDateRange',
			'purchaseOrderFollowUpDateRange',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/purchase-orders/:id',
			objectLabel: 'Purchase Order',
			objectTable: 'purchase_order',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'date', fieldLabel: 'Order Date' },
				{ fieldName: 'requestedDate', fieldLabel: 'Requested Date' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
			],
		},
		{
			// PD-14564: registers the vendor object so the Vendor column's
			// object-link resolves to /vendors/:id. Without it the link
			// resolver (useListLinkNavigation) falls back to the first object
			// (purchase_order) and mislinks the vendor name to /purchase-orders/:id.
			baseUrl: '/vendors/:id',
			objectLabel: 'Vendor',
			objectTable: 'vendor',
			selectedFields: [],
		},
	],
	tableSorts: [
		{
			id: 'po-number',
			name: 'PO Number',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'externalId',
				},
			],
			description: 'Sort by PO number',
		},
		{
			id: 'status',
			name: 'Status',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'status',
				},
			],
			description: 'Sort by status',
		},
		{
			id: 'order-date',
			name: 'Order Date',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'date',
				},
			],
			description: 'Sort by order date (newest first)',
		},
		{
			id: 'requested-date',
			name: 'Requested Date',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'requestedDate',
				},
			],
			description: 'Sort by requested date (newest first)',
		},
	],
};
